import { Component } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { Subject, from, map, mergeMap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ACCOUNT_LOGIN_ROUTE,
  CONTINUE_WITH_APPLE_SIGN_IN_ROUTE, CONTINUE_WITH_APPLE_SIGN_UP_ROUTE,
  CONTINUE_WITH_GOOGLE_SIGN_IN_ROUTE,
  CONTINUE_WITH_GOOGLE_SIGN_UP_ROUTE,
  DEFAULT_ROUTE_CONSTANT,
  DESTINATION_KEY_CONSTANT,
  EMAIL_VERIFICATION_CTA_ROUTE,
  FRESH_FROM_SOURCE_CONSTANT,
  LOGIN_OBJECT_KEY_CONSTANT,
  PREVIOUS_DESTINATION_KEY_CONSTANT,
  PRODUCT_KEY_CONSTANT,
  REDIRECTED_FLAG_CONSTANT,
} from '../constants/routes.constant';
import { UserProfileService } from '../services/user-profile.service';
import { ONBOARDING_ID_TOKEN_KEY } from '../constants/local-storage.constant';
import { PROGRESS_BAR_SUBJECT } from '../app.component';
import { HttpStatusCode } from "@angular/common/http";

@Component({
    selector: 'app-create-account',
    templateUrl: './create-account.component.html',
    styleUrls: ['./create-account.component.scss'],
    standalone: false
})
export class CreateAccountComponent {
  public hide: boolean = true;
  public inProgress: boolean = false;
  public form!: FormGroup;
  public verifyEmailCtaRoute: string = EMAIL_VERIFICATION_CTA_ROUTE;
  public errorText =
    'Unfortunately, we could not create your account. Kindly sign in if you have an account already.';
  public showError: boolean = false;
  private loginRoute: string = ACCOUNT_LOGIN_ROUTE;
  private productName!: string;
  private destination!: string;
  private loginObject!: string;
  private previousDestination!: string;
  private productNameKey: string = PRODUCT_KEY_CONSTANT;
  private destinationKey: string = DESTINATION_KEY_CONSTANT;
  private loginObjectKey: string = LOGIN_OBJECT_KEY_CONSTANT;
  private previousDestinationKey: string = PREVIOUS_DESTINATION_KEY_CONSTANT;
  public queryParams: { [key: string]: string } = {
    [this.productNameKey]: this.productName,
    [this.destinationKey]: this.destination,
    [this.loginObjectKey]: this.loginObject,
    [this.previousDestinationKey]: this.previousDestination,
  };
  public subject: Subject<boolean> = PROGRESS_BAR_SUBJECT;
  private signUpWithGoogleRoute: string = CONTINUE_WITH_GOOGLE_SIGN_UP_ROUTE;
  private signUpWithAppleRoute: string = CONTINUE_WITH_APPLE_SIGN_UP_ROUTE;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly firebaseAuthService: FirebaseAuthService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly userProfileService: UserProfileService,
    private readonly activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    // TODO to be removed later
    localStorage.removeItem(`${REDIRECTED_FLAG_CONSTANT}fc`);

    this.initializeForm();

    this.productName = this.activatedRoute.snapshot.queryParamMap.get(
      this.productNameKey
    )!;
    this.destination = this.activatedRoute.snapshot.queryParamMap.get(
      this.destinationKey
    )!;
    this.loginObject = this.activatedRoute.snapshot.queryParamMap.get(
      this.loginObjectKey
    )!;
    this.previousDestination = this.activatedRoute.snapshot.queryParamMap.get(
      this.previousDestinationKey
    )!;

    this.queryParams = {
      [this.productNameKey]: this.productName,
      [this.destinationKey]: this.destination,
      [this.loginObjectKey]: this.loginObject,
      [this.previousDestinationKey]: this.previousDestination,
    };

    if (!this.productName || !this.destination) {
      this.router.navigate(DEFAULT_ROUTE_CONSTANT, {
        queryParams: this.queryParams,
        queryParamsHandling: 'preserve',
      });
    }
  }

  public static removeOnboardingToken() {
    localStorage.removeItem(ONBOARDING_ID_TOKEN_KEY);
  }

  public get getLoginRoute() {
    return ['/', ...this.loginRoute.split('/')];
  }

  public signUpWithEmailAndPassword() {
    this.subject.next(true);
    this.inProgress = true;
    this.showError = false;

    const value = this.form.getRawValue();

    this.createUserProfile(value)
      .subscribe({
        next: (idTokenResult) => {
          this.subject.next(false);
          localStorage.setItem(ONBOARDING_ID_TOKEN_KEY, 'idTokenResult.token');

          // INFO Route to verify email page
          this.router.navigate(['/', ...this.verifyEmailCtaRoute.split('/'), btoa(value.email)], {
            queryParams: this.queryParams,
          });
          this.form.reset();
          this.inProgress = false;
        },
        error: (error) => {
          const emailExists = error.code?.toLowerCase() == 'auth/email-already-in-use';
          const errorMessage = emailExists ?
            'Account already exists' :
            error.status || error.status === HttpStatusCode.InternalServerError ?
              'An error occurred while creating your account. Kindly contact support.' : 'An error occurred!';

          this.subject.next(false);
          this.inProgress = false;
          this.snackBar.open(errorMessage, 'close', {
            verticalPosition: 'top',
            horizontalPosition: 'end',
            panelClass: [
              'bg-danger',
              'text-white',
              'rounded-3e',
              'modified-snackbar',
            ],
          });
          this.showError = true;

          if (emailExists) {
            this.router.navigate(['/', ...this.loginRoute.split('/')], {
              queryParams: this.queryParams,
            });
          } else  {
            this.router.navigate(['/', ...this.loginRoute.split('/')], {
              queryParams: this.queryParams,
            });
          }
        },
      });
  }

  public createUserProfile(token: string) {
    return this.userProfileService.createUserProfileStandalone(token, this.productName, this.destination);
  }

  public signUpWithApple() {
    const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };

    return this.router.navigate(
      ['/', ...this.signUpWithAppleRoute.split('/')],
      {
        queryParams,
        queryParamsHandling: 'merge',
      }
    );
  }

  public signUpWithGoogle() {
    const queryParams = { ...this.queryParams, [FRESH_FROM_SOURCE_CONSTANT]: 'true' };

    return this.router.navigate(
      ['/', ...this.signUpWithGoogleRoute.split('/')],
      {
        queryParams,
        queryParamsHandling: 'merge',
      }
    );
  }

  public initializeForm() {
    this.form = this.formBuilder.nonNullable.group({
      name: new FormControl(null, [Validators.required]),
      email: new FormControl(null, [Validators.required, Validators.email]),
      password: new FormControl(null, [
        Validators.required,
        Validators.minLength(8),
      ]),
    });
  }
}
